"use client";

import * as React from "react";
import { Check, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Payment = {
  id: string;
  amountToman: number;
  receiptImage: string | null;
  plan: { name: string };
};

export function UploadReceiptDialog({
  payment,
  onClose,
  onUploaded,
}: {
  payment: Payment;
  onClose: () => void;
  onUploaded: (path: string) => void;
}) {
  const [path, setPath] = React.useState<string | null>(payment.receiptImage);
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const onFile = async (file?: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("حداکثر حجم فایل ۵ مگابایت است");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data.path) {
        throw new Error(data.error || "آپلود ناموفق بود");
      }
      setPath(data.path);
      toast.success("تصویر آپلود شد");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "آپلود ناموفق بود";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!path) {
      toast.error("ابتدا تصویر را آپلود کنید");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/payments", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: payment.id, receiptImage: path }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "ذخیره ناموفق بود");
      toast.success("فیش با موفقیت ثبت شد");
      onUploaded(path);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ذخیره ناموفق بود";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>آپلود فیش واریزی</DialogTitle>
          <DialogDescription>
            برای طرح «{payment.plan.name}»
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {path ? (
            <div className="space-y-2">
              <img
                src={path}
                alt="فیش واریزی"
                className="max-h-60 w-full rounded-xl bg-muted/30 object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setPath(null)}
                disabled={uploading || saving}
              >
                تغییر تصویر
              </Button>
            </div>
          ) : (
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/20 px-3 py-8 text-center transition hover:bg-muted/40">
              {uploading ? (
                <Loader2 className="size-6 animate-spin text-primary" />
              ) : (
                <Upload className="size-6 text-primary" />
              )}
              <span className="text-sm text-muted-foreground">
                {uploading ? "در حال آپلود..." : "تصویر فیش را انتخاب کنید"}
              </span>
              <span className="text-[10px] text-muted-foreground">
                jpg / png / webp — حداکثر ۵ مگابایت
              </span>
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={uploading || saving}
                onChange={(e) => onFile(e.target.files?.[0])}
              />
            </label>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            انصراف
          </Button>
          <Button onClick={onSave} disabled={saving || uploading || !path} className="gap-2">
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            {saving ? "در حال ذخیره..." : "ثبت فیش"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UploadReceiptDialog;
